/**
 * Pieces shared across the marketing screens: the channel pill, the per-channel
 * setup form, and the opt-out editor that appears in more than one place.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import WhatsAppTemplates from './WhatsAppTemplates.jsx';
import WhatsAppSetup from './WhatsAppSetup.jsx';
import PushSetup from './PushSetup.jsx';
import Icon from '../../lib/icons.jsx';
import { Pill } from '../../components/Primitives.jsx';
import { CHANNELS, channelLabel, channelIcon, channelPill } from '../../constants/marketing.js';

export const ChannelPill = ({ channel }) => (
  <Pill tone={channelPill(channel)} icon={channelIcon(channel)}>{channelLabel(channel)}</Pill>
);

export function CampaignStatusPill({ status }) {
  const tone = status === 'Sent' ? 'p-green'
    : status === 'Sending' ? 'p-amber'
      : status === 'Scheduled' ? 'p-blue' : 'p-grey';
  return <Pill tone={tone}>{status}</Pill>;
}

/** A labelled text input bound to one key under `settings.mkt`. */
function ChannelField({ name, label, hint, type }) {
  const { S, setChannelSetting } = useApp();
  const value = (S.mkt || {})[name];
  return (
    <div className="num" style={{ marginBottom: 12 }}>
      <label>{label}</label>
      <input
        type={type}
        value={value === undefined ? '' : value}
        onChange={e => setChannelSetting(name, type === 'number' ? Number(e.target.value) : e.target.value)}
      />
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

/**
 * Configuration for one channel.
 *
 * The copy is deliberate about what actually happens: nothing here sends in
 * bulk by itself. Messages open in the user's own mail client or WhatsApp,
 * which is honest about the capability rather than implying a delivery
 * pipeline that does not exist yet.
 */
export function ChannelConfig({ channel }) {
  const { toast } = useApp();
  const [, force] = useState(0);

  /*
   * Email has nothing left here. The name on a message used to be one setting
   * for the whole workspace, which stopped making sense once a campaign could
   * go out through any of several mailboxes: the recipient sees whichever one
   * actually sent, so the identity belongs to the account, beside its
   * connection, and MailSetup owns both.
   */
  if (channel === 'email') return null;

  if (channel === 'whatsapp') {
    return (
      <>
        <WhatsAppSetup />
        <h5 className="wah">Default template for new campaigns</h5>
        <WhatsAppTemplates />
      </>
    );
  }

  if (channel === 'sms') {
    return (
      <>
        <p className="muted">
          Sender IDs in the UAE must be registered with your aggregator, and marketing SMS
          needs recorded consent under TDRA rules.
        </p>
        <ChannelField name="smsSender" label="Sender ID" />
        <ChannelField
          name="smsLimit"
          label="Characters per message"
          hint="longer messages split and cost more"
          type="number"
        />
      </>
    );
  }

  return (
    <>
      <p className="muted">
        Push reaches the XYRA apps through Firebase Cloud Messaging. Connect the project&rsquo;s
        service account, name the topic your apps subscribe users to, and a campaign is a
        notification Firebase fans out to every subscribed device.
      </p>
      <PushSetup />
    </>
  );
}

/** The opt-out list, editable. Shown on Channel setup and on each channel page. */
export function OptOutEditor() {
  const { mkt, addOptOut, removeOptOut } = useApp();
  const [text, setText] = useState('');

  return (
    <div className="chips">
      {mkt.optouts.map((o, i) => (
        <span className="chip" key={o}>
          {o}
          <button type="button" onClick={() => removeOptOut(i)}>✕</button>
        </span>
      ))}
      <input
        placeholder="Add an email or number…"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          addOptOut(text);
          setText('');
        }}
      />
    </div>
  );
}

export { CHANNELS };
